import React, { useEffect, useState } from "react";
import { Col, Container, FormGroup, Row } from "react-bootstrap";
import { useFunctions } from "reactfire";
import BarLoader from "react-spinners/BarLoader";
import { Country, UserRole } from "../../../../shared/constants/Constant";
import { UserSignUpDTO } from "../../../../database/models/UserSignUpDTO";
import FormValidator from "../../../../shared/utils/FormValidator";
import "../../../registration/containers/styles.scss";
import Alert from "../../../../shared/components/alert/Alert";
import SuccsessAlert from "../../../../shared/components/alert/successAlert";
import { useAppUser } from "../../../../contexts/user-context/UserContext";
import CheckBox from "../../../../shared/components/ui/check-box/CheckBox";

interface IProps {
  userRole: UserRole;
}

enum UserCountrySelect {
  SELECT_COUNTRY = "",
  SRI_LANKA = "SRI_LANKA",
  MALAYSIA = "MALAYSIA",
  BOSNIA = "BOSNIA",
  JAMAICA = "JAMAICA"
}

interface IState {
  uid?: string;
  fullName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  loading: boolean;
  isAgree: boolean;
  isAgreeError: string;
  fullNameError: string;
  usernameError: string;
  emailError: string;
  passwordError: string;
  confirmPasswordError: string;
  country?: Country | undefined;
  countryError: string;
}

const initState: IState = {
  uid: "",
  fullName: "",
  username: "",
  email: "",
  password: "",
  confirmPassword: "",
  loading: false,
  isAgree: false,
  isAgreeError: "",
  fullNameError: "",
  usernameError: "",
  emailError: "",
  passwordError: "",
  confirmPasswordError: "",
  country: undefined,
  countryError: "",
};

interface AlertMessage {
  hasError: boolean;
  isSuccess: boolean;
}

const initMessage: AlertMessage = {
  hasError: false,
  isSuccess: false,
};

const RegistrationForm = (props: IProps) => {
  const [state, setState] = useState(initState);
  const [msgState, setMessageState] = useState(initMessage);
  const appUser = useAppUser();

  const clearState = () => {
    if (appUser.userRole.isAdmin && !props.userRole.isAdmin) {
      const countrySelector: HTMLOptionElement = document.getElementById(
        "country-selector"
      ) as HTMLOptionElement;
      countrySelector.value = UserCountrySelect.SELECT_COUNTRY;
    }

    setState(initState);
  };

  const closeNotifications = () => {
    setMessageState({ ...msgState, isSuccess: false, hasError: false });
  };

  const signUpRef = useFunctions().httpsCallable("signUpUser");

  const validateRegisterFormData = (): boolean => {
    const fnValidation = FormValidator.validateString(
      state.fullName,
      "Full name",
      3,
      100
    );
    const unValidation = FormValidator.validateString(
      state.username,
      "Username",
      3,
      20
    );
    const emailValidation = FormValidator.validateEmail(state.email);
    const pwValidation = FormValidator.validateString(
      state.password,
      "Password",
      6,
      20
    );
    const countryValidation =
      appUser.userRole.isAdmin && !props.userRole.isAdmin
        ? !state.country
          ? { message: "Select moderator's country", isValid: false }
          : { message: "", isValid: true }
        : { message: "", isValid: true };

    const cpValidation =
      state.password !== state.confirmPassword
        ? { message: "Confirmed password is invalid.", isValid: false }
        : { message: "", isValid: true };

    const isAgreeValidation = !state.isAgree
      ? {
          message: "You must agree to the policies to continue.",
          isValid: false,
        }
      : { message: "", isValid: true };

    setState({
      ...state,
      fullNameError: fnValidation.message,
      usernameError: unValidation.message,
      emailError: emailValidation.message,
      passwordError: pwValidation.message,
      confirmPasswordError: cpValidation.message,
      isAgreeError: isAgreeValidation.message,
      countryError: countryValidation.message,
    });

    return (
      fnValidation.isValid &&
      unValidation.isValid &&
      emailValidation.isValid &&
      pwValidation.isValid &&
      cpValidation.isValid &&
      isAgreeValidation.isValid &&
      countryValidation.isValid
    );
  };

  const signUp = () => {
    setState((load) => ({ ...load, loading: true }));

    const userData: UserSignUpDTO = {
      email: state.email,
      displayName: state.fullName,
      username: state.username,
      password: state.password,
      userRole: props.userRole,
      country:
        appUser.userRole.isAdmin && !props.userRole.isAdmin
          ? state.country || Country.SRI_LANKA
          : appUser.country,
    };

    signUpRef(userData)
      .then((data) => {
        setMessageState((ps) => ({ ...ps, isSuccess: true, hasError: false }));
        setState((ps) => ({ ...ps, loading: false }));
        clearState();
      })
      .catch((error) => {
        const errorMessage: string = error.message;
        setMessageState((ps) => ({ ...ps, hasError: true, isSuccess: false }));
        if (errorMessage.includes("already in use")) {
          setState({
            ...state,
            emailError: "This email is already in use.",
            loading: false,
          });
        } else {
          setState((ps) => ({ ...ps, loading: false }));
          clearState();
        }
      });
  };

  useEffect(() => {
    let timer1 = setTimeout(
      () => setMessageState((ps) => ({ ...ps, isSuccess: false })),
      10000
    );
    return () => {
      clearTimeout(timer1);
    };
  }, [msgState.isSuccess]);

  return (
    <Container className="h-100">
      <Row className="justify-content-center" style={{ height: "90%" }}>
        <Col
          xs={12}
          className="d-flex justify-content-center"
          style={{ marginTop: "15%" }}
        >
          <div style={{ width: "950px" }}>
            <form
              onSubmit={(e: any) => {
                e.preventDefault();
                validateRegisterFormData() && signUp();
              }}
            >
              <FormGroup className="mb-5">
                <h1
                  className="text-center color-white"
                  style={{ fontSize: "30px" }}
                >
                  Create{" "}
                  {props.userRole.isAdmin === true
                    ? "an administrator account"
                    : "a moderator account"}
                </h1>
              </FormGroup>

              {msgState.hasError && (
                <FormGroup>
                  <Alert show={msgState.hasError} onClose={closeNotifications}>
                    <b>An Error Occurred!</b>
                  </Alert>
                </FormGroup>
              )}

              {msgState.isSuccess && (
                <FormGroup>
                  <SuccsessAlert
                    show={msgState.isSuccess}
                    onClose={closeNotifications}
                  >
                    <b>User Created Successfully!</b>
                  </SuccsessAlert>
                </FormGroup>
              )}

              <div style={{ paddingLeft: "75px", paddingRight: "75px" }}>
                <FormGroup>
                  <input
                    className="register-input"
                    placeholder="Full Name"
                    value={state.fullName}
                    onChange={(e) => {
                      setState({
                        ...state,
                        fullName: e.target.value,
                        fullNameError: "",
                      });
                      closeNotifications();
                    }}
                  />
                  <label className="error-message-glow">
                    {state.fullNameError}
                  </label>
                </FormGroup>

                <Container fluid className="h-100">
                  <Row className="h-100 align-content-right">
                    <Col className="text-left pl-0 pr-4" xs={6}>
                      <FormGroup>
                        <input
                          className="register-input"
                          placeholder="Username"
                          value={state.username}
                          onChange={(e) => {
                            setState({
                              ...state,
                              username: e.target.value,
                              usernameError: "",
                            });
                            closeNotifications();
                          }}
                        />
                        <label className="error-message-glow">
                          {state.usernameError}
                        </label>
                      </FormGroup>
                    </Col>

                    <Col className="text-left pl-4 pr-0" xs={6}>
                      <FormGroup>
                        <input
                          className="register-input"
                          placeholder="Email"
                          value={state.email}
                          onChange={(e) => {
                            setState({
                              ...state,
                              email: e.target.value,
                              emailError: "",
                            });
                            closeNotifications();
                          }}
                        />
                        <label className="error-message-glow">
                          {state.emailError}
                        </label>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="align-content-right">
                    <Col className="text-left pl-0 pr-4" xs={6}>
                      <FormGroup>
                        <input
                          className="register-input"
                          placeholder="Password"
                          type="password"
                          value={state.password}
                          onChange={(e) => {
                            setState({
                              ...state,
                              password: e.target.value,
                              passwordError: "",
                            });
                            closeNotifications();
                          }}
                        />
                        <label className="error-message-glow">
                          {state.passwordError}
                        </label>
                      </FormGroup>
                    </Col>

                    <Col className="text-left pl-4 pr-0" xs={6}>
                      <FormGroup>
                        <input
                          className="register-input"
                          placeholder="Confirm Password"
                          type="password"
                          value={state.confirmPassword}
                          onChange={(e) => {
                            setState({
                              ...state,
                              confirmPassword: e.target.value,
                              confirmPasswordError: "",
                            });
                            closeNotifications();
                          }}
                        />
                        <label className="error-message-glow">
                          {state.confirmPasswordError}
                        </label>
                      </FormGroup>
                    </Col>
                  </Row>

                  {appUser.userRole.isAdmin && !props.userRole.isAdmin && (
                    <Row className="align-content-right">
                      <Col className="text-left pl-0 pr-4" xs={6}>
                        <FormGroup>
                          <select
                            id="country-selector"
                            className="register-input"
                            placeholder="Select Country"
                            defaultValue={UserCountrySelect.SELECT_COUNTRY}
                            onChange={(e) => {
                              if (
                                appUser.userRole.isAdmin &&
                                !props.userRole.isAdmin
                              ) {
                                setState({
                                  ...state,
                                  country: e.target.value as Country,
                                  countryError: "",
                                });
                              }
                              closeNotifications();
                            }}
                          >
                            <option
                              value={UserCountrySelect.SELECT_COUNTRY}
                              hidden
                            >
                              Select Country
                            </option>
                            <option
                              className="option-input"
                              value={UserCountrySelect.SRI_LANKA}
                            >
                              Sri Lanka
                            </option>
                            <option
                              className="option-input"
                              value={UserCountrySelect.MALAYSIA}
                            >
                              Malaysia
                            </option>
                            <option
                              className="option-input"
                              value={UserCountrySelect.BOSNIA}
                            >
                              Bosnia and Herzegovina
                            </option>
                            <option
                              className="option-input"
                              value={UserCountrySelect.JAMAICA}
                            >
                              Jamaica
                            </option>
                          </select>
                          <label className="error-message-glow">
                            {state.countryError}
                          </label>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                </Container>

                <Container fluid className="mt-3">
                  <Row className="align-content-end">
                    <Col className="text-left pl-0" xs={9}>
                      <CheckBox
                        checked={state.isAgree}
                        label={
                          <span>
                            I agree to the terms of use and acknowledge the{" "}
                            <br />
                            privacy policy
                          </span>
                        }
                        onSelect={(b) => {
                          setState({
                            ...state,
                            isAgree: b,
                            isAgreeError: "",
                          });
                          closeNotifications();
                        }}
                      />

                      <label className="error-message-glow">
                        {state.isAgreeError}
                      </label>
                    </Col>
                    <Col className="text-right pr-0">
                      <FormGroup className="text-right pt-1">
                        <button
                          disabled={state.loading}
                          className={state.loading ? "register-button-disabled" : "register-button"}
                          type="submit"
                        >
                          Sign Up
                        </button>
                      </FormGroup>
                    </Col>
                  </Row>
                </Container>
                {state.loading && (
                  <FormGroup>
                    <BarLoader
                      height={4}
                      width={"100%"}
                      color={"#fff"}
                      loading={state.loading}
                    />
                  </FormGroup>
                )}
              </div>
            </form>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default RegistrationForm;
